import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { LOGO_WORDMARK } from "./data";
import { SizePicker } from "./SizePicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Signup = () => {
  const [email, setEmail] = useState("");
  const [size, setSize] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/subscribe/count`)
      .then((r) => setCount(r.data.count))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email: value, size });
      setDone(true);
      setCount((c) => (c === null ? 1 : c + 1));
      toast.success("You're in. Check your inbox for your welcome note.");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        setDone(true);
        toast(msg || "You're already on the list.");
      } else {
        toast.error(msg || "Something went wrong. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="email-signup-section"
      className="relative py-24 sm:py-36 bg-[#f3f2ec] overflow-hidden"
    >
      <img
        src={LOGO_WORDMARK}
        alt=""
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] max-w-[700px] opacity-[0.04]"
      />
      <div className="absolute inset-0 hero-radial" />

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 max-w-2xl mx-auto px-5 sm:px-8 text-center"
      >
        <span className="inline-flex items-center gap-2 font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#ff2a85] border border-[#ff2a85]/30 rounded-full px-4 py-2 mb-8">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#ff2a85] opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#ff2a85]" />
          </span>
          Pre-Order Access
        </span>

        <h2 className="font-display font-extrabold uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl tracking-tight">
          Be first <span className="text-pink">to the drop.</span>
        </h2>
        <p className="mt-5 text-[#5f5e5a] text-base sm:text-lg leading-relaxed max-w-lg mx-auto">
          The Origin Collection is extremely limited. Join the pre-order list
          for first access, exclusive previews, and the launch moment.
        </p>

        {done ? (
          <motion.div
            data-testid="signup-success"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-10 inline-flex flex-col items-center gap-4 bg-[#faf9f6] border border-[#ff2a85]/30 rounded-2xl px-10 py-8 pink-glow"
          >
            <div className="h-12 w-12 rounded-full bg-[#ff2a85] flex items-center justify-center">
              <Check size={22} className="text-white" strokeWidth={3} />
            </div>
            <p className="font-display font-extrabold uppercase text-xl">
              You're on the list
            </p>
            <p className="text-[#5f5e5a] text-sm">
              Your welcome note is on its way. Deen over Dunya.
            </p>
          </motion.div>
        ) : (
          <form
            data-testid="signup-form"
            onSubmit={submit}
            className="mt-10 max-w-md mx-auto"
          >
            <p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#5f5e5a] mb-3">
              Your size <span className="text-[#5f5e5a]/60">(optional — helps us plan production)</span>
            </p>
            <div className="flex justify-center mb-6">
              <SizePicker value={size} onChange={setSize} testPrefix="signup-size" />
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full sm:flex-1 h-14 px-5 rounded-full bg-[#faf9f6] border border-[#e3e2dc] text-[#0a0a0b] placeholder:text-[#5f5e5a]/60 text-sm outline-none focus:border-[#ff2a85]/60 transition-colors"
            />
            <button
              data-testid="email-submit-button"
              type="submit"
              disabled={loading}
              className="h-14 px-8 rounded-full bg-[#ff2a85] text-white font-mono-d text-[11px] uppercase tracking-[0.2em] font-medium hover:bg-[#e60067] disabled:opacity-60 transition-all duration-300 flex items-center justify-center gap-2 shrink-0"
            >
              {loading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                "Secure My Spot"
              )}
            </button>
            </div>
          </form>
        )}

        {count !== null && count > 0 && (
          <p
            data-testid="signup-count"
            className="mt-6 font-mono-d text-[11px] uppercase tracking-[0.2em] text-[#5f5e5a]"
          >
            {count.toLocaleString()} already waiting on the drop
          </p>
        )}
      </motion.div>
    </section>
  );
};
